import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendar.css';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import { secureGet, secureDelete } from '../../config/api';
import { PrimaryButton } from '../../components/PrimaryButton';
import AppointmentForm from './AppointmentForm';
import AppointmentDetails from './AppointmentDetails';
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from '../../Shared/UserContext';
import LoadingLogo from '../../components/LoadingLogo';

import AddIcon from '../../icons/plusB.svg';
import SearchIcon from '../../icons/search.svg';
import CalendarIconSvg from '../../icons/calendar.svg';

moment.locale('fr');
const localizer = momentLocalizer(moment);

const Calendar = () => {
  const { t } = useTranslation();
  const { state: { role } } = useContext(UserContext);  
  
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [todayAppointmentsCount, setTodayAppointmentsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Initialize with current month's first day
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [currentView, setCurrentView] = useState('month');
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    // Return date format: yyyy-MM-dd
    return `${year}-${month}-${day}`;
  };

  const getDateRange = useCallback((date, view) => {
    const m = moment(date);
    let startDate, endDate;

    switch (view) {
      case 'day':
        // For day view: same start and end date
        startDate = m.clone().startOf('day');
        endDate = m.clone().startOf('day');
        break;
      case 'week':
        // For week view: start of week to end of week
        startDate = m.clone().startOf('week');
        endDate = m.clone().endOf('week');
        break;
      case 'month':
      default:
        // For month view: first date of month to last date of month
        startDate = m.clone().startOf('month');
        endDate = m.clone().endOf('month');
        break;
    }

    const startDateObj = startDate.startOf('day').toDate();
    const endDateObj = endDate.endOf('day').toDate();

    return {
      startDate: formatDateForAPI(startDateObj),
      endDate: formatDateForAPI(endDateObj),
    };
  }, []);

  // Fetch appointments by date range
  const fetchAppointmentsByDateRange = useCallback(async (date, view) => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(date, view);
      const response = await secureGet(
        `/appointments/date-range?startDate=${startDate}&endDate=${endDate}`,
        showSnackbar
      );
      const appointmentsData = response.data.content || response.data || [];
      const formattedAppointments = appointmentsData.map(apt => {
        // Build title as "description - client - vehicle"
        const titleParts = [];
        if (apt.title) titleParts.push(apt.title);
        if (apt.clientName) titleParts.push(apt.clientName);
        if (apt.vehicleImmatriculationNumber) titleParts.push(apt.vehicleImmatriculationNumber);
        const title = titleParts.join(' - ') || apt.title || '';
        
        return {
          id: apt.id,
          title: title,
          start: new Date(apt.appointmentDate),
          end: new Date(new Date(apt.appointmentDate).getTime() + 60 * 60 * 1000), // 1 hour duration
          clientId: apt.clientId,
          clientName: apt.clientName,
          vehicleId: apt.vehicleId,
          vehicleImmatriculationNumber: apt.vehicleImmatriculationNumber,
          description: apt.description,
          createdAt: apt.createdAt,
          createdById: apt.createdById,
          createdByUsername: apt.createdByUsername,
        };
      });
      setAppointments(formattedAppointments);
      setFilteredAppointments(formattedAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      showSnackbar(t('errors.fetch_appointments'), 'error');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, t]);

  // Fetch today's appointments count
  const fetchTodayAppointmentsCount = useCallback(async () => {
    try {
      const today = moment();
      const startDateObj = today.clone().startOf('day').toDate();
      const endDateObj = today.clone().endOf('day').toDate();
      const startDate = formatDateForAPI(startDateObj);
      const endDate = formatDateForAPI(endDateObj);
      const response = await secureGet(
        `/appointments/date-range?startDate=${startDate}&endDate=${endDate}`,
        null // Don't show snackbar for this silent fetch
      );
      const appointmentsData = response.data.content || response.data || [];
      setTodayAppointmentsCount(appointmentsData.length);
    } catch (error) {
      console.error('Error fetching today appointments count:', error);
    }
  }, []);

  useEffect(() => {
    fetchAppointmentsByDateRange(selectedDate, currentView);
    fetchTodayAppointmentsCount();
  }, [selectedDate, currentView, fetchAppointmentsByDateRange, fetchTodayAppointmentsCount]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAppointments(appointments);
    } else {
      const filtered = appointments.filter(apt => 
        apt.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAppointments(filtered);
    }
  }, [searchTerm, appointments]);


  const handleSelectSlot = ({ start, end }) => {
    setSelectedAppointment({
      start,
      end,
      title: '',
      clientId: '',
      vehicleId: '',
      description: '',
    });
    setShowAppointmentForm(true);
  };

  const handleSelectEvent = async (event) => {
    try {
      const response = await secureGet(`/appointments/${event.id}`, showSnackbar);
      setSelectedAppointment(response.data);
      setShowAppointmentDetails(true);
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      setSelectedAppointment(event);
      setShowAppointmentDetails(true);
    }
  };

  const handleNewAppointment = () => {
    const now = new Date();
    setSelectedAppointment({
      start: now,
      title: '',
      clientId: '',
      vehicleId: '',
      description: '',
    });
    setShowAppointmentForm(true);
  };

  const handleSaveAppointment = (appointmentData) => {
    fetchAppointmentsByDateRange(selectedDate, currentView);
    fetchTodayAppointmentsCount();
    setShowAppointmentForm(false);
    setSelectedAppointment(null);
  };

  const handleDetailsRefresh = () => {
    fetchAppointmentsByDateRange(selectedDate, currentView);
    fetchTodayAppointmentsCount();
  };

  const eventStyleGetter = (event) => {
    return {};
  };

  const messages = {
    allDay: 'Toute la journée',
    previous: 'Précédent',
    next: 'Suivant',
    today: "Aujourd'hui",
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Événement',
    noEventsInRange: 'Aucun rendez-vous dans cette période.',
    showMore: (total) => `${total} AUTRE...`,
  };

  const formats = {
    dayHeaderFormat: (date) => moment(date).format('dddd'),
    monthHeaderFormat: (date) => moment(date).format('MMMM YYYY'),
    dayRangeHeaderFormat: ({ start, end }) => 
      `${moment(start).format('DD MMM')} - ${moment(end).format('DD MMM YYYY')}`,
  };

  return (
    <div className="">
      <Helmet>
        <title>{t('titles.calendar')} - DrivNest</title>
      </Helmet>

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-[32px] font-bold text-[#16283E]">
              {t('titles.calendar')}
            </h1>
            <p className="text-[14px] text-primary font-semibold">
              {todayAppointmentsCount} {t('calendar.appointments_today')}
            </p>
          </div>
          {(role === 'ROLE_ADMIN' || role === 'ROLE_SERVICE_CONSULTANT') && (
            <PrimaryButton
              label={t('primaryButtons.new_appointment')}
              icon={AddIcon}
              onClick={handleNewAppointment}
            />
          )}
        </div>
      </div>

      {/* Calendar */}
      <div className="relative">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <LoadingLogo />
          </div>
        ) : (
          <BigCalendar
            localizer={localizer}
            events={filteredAppointments}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 850 }}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            selectable
            eventPropGetter={eventStyleGetter}
            messages={messages}
            formats={formats}
            culture="fr"
            defaultView="month"
            views={['month', 'week', 'day', 'agenda']}
            date={selectedDate}
            view={currentView}
            onNavigate={(date) => {
              setSelectedDate(date);
            }}
            onView={(view) => {
              setCurrentView(view);
            }}
          />
        )}
      </div>

      {/* Appointment Form Modal */}
      {showAppointmentForm && (
        <AppointmentForm
          appointment={selectedAppointment}
          onSave={handleSaveAppointment}
          onClose={() => setShowAppointmentForm(false)}
        />
      )}

      {/* Appointment Details Modal */}
      {showAppointmentDetails && selectedAppointment && (
        <AppointmentDetails
          appointment={selectedAppointment}
          onClose={() => setShowAppointmentDetails(false)}
          onRefresh={handleDetailsRefresh}
        />
      )}

      {/* Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default Calendar;

